import { config } from '../config';
import { getAuthToken, AUTH_SESSION_EXPIRED } from '../context/AuthContext';

const baseUrl = config.apiBaseUrl;

const API_URL_NOT_SET_MSG =
  'API URL is not set. Set VITE_API_BASE_URL in Amplify (Hosting → Environment variables), then redeploy.';

export async function api<T>(
  path: string,
  options?: RequestInit & { params?: Record<string, string> }
): Promise<T> {
  const effectiveBase = (baseUrl || '').trim();
  if (!effectiveBase) {
    throw new Error(API_URL_NOT_SET_MSG);
  }
  const { params, ...rest } = options ?? {};
  const base = effectiveBase.replace(/\/$/, '') + '/';
  const url = new URL(path.startsWith('/') ? path.slice(1) : path, base);
  if (params) {
    Object.entries(params).forEach(([k, v]) => url.searchParams.set(k, v));
  }
  const res = await fetch(url.toString(), {
    ...rest,
    headers: { 'Content-Type': 'application/json', ...rest.headers },
  });
  if (!res.ok) throw new Error(await res.text().catch(() => res.statusText));
  return res.json() as Promise<T>;
}

export type ListingListParams = {
  type?: string;
  status?: string;
  limit?: string;
  nextToken?: string;
  location?: string;
  country?: string;
  minPrice?: string | number;
  maxPrice?: string | number;
  bedrooms?: string | number;
  category?: string;
  unitType?: string;
  /** Comma-separated amenity keys, e.g. "pool,gym_health_club" */
  amenities?: string;
  sort?: 'newest' | 'price' | 'area' | 'bedrooms';
  order?: 'asc' | 'desc';
};

export const listings = {
  list: (params?: ListingListParams) => {
    const normalized: Record<string, string> = {};
    if (params) {
      for (const [k, v] of Object.entries(params)) {
        if (v != null && v !== '') normalized[k] = String(v);
      }
    }
    return api('/public/listings', {
      params: Object.keys(normalized).length ? normalized : undefined,
    }) as Promise<ListingsResponse>;
  },
  get: (id: string) => api(`/public/listings/${id}`) as Promise<Listing>,
  getMediaUrls: (id: string) => api(`/public/listings/${id}/media/urls`) as Promise<{ urls: string[] }>,
  createInquiry: (
    listingId: string,
    body: { name: string; email: string; phone?: string; message: string; source?: 'listing_detail' | 'card_cta' | 'whatsapp' | 'callback' }
  ) =>
    api(`/public/listings/${listingId}/inquiries`, {
      method: 'POST',
      body: JSON.stringify(body),
    }) as Promise<{ inquiryId: string }>,
};

/** Create a saved search alert (notify when new listings match filters). */
export async function createAlertApi(email: string, filters: Record<string, unknown>): Promise<{ alertId: string }> {
  return api('/public/alerts', {
    method: 'POST',
    body: JSON.stringify({ email, filters }),
  }) as Promise<{ alertId: string }>;
}

/** Resend sign-up verification code (public; uses backend so it works without Cognito "auto verification"). */
export async function resendVerificationCodeApi(email: string): Promise<void> {
  if (!baseUrl?.trim()) {
    throw new Error(
      'Verification resend is not configured. Set VITE_API_BASE_URL (Amplify: Hosting → Environment variables → add VITE_API_BASE_URL = your API URL, then redeploy).'
    );
  }
  try {
    await api<{ message?: string }>('/public/resend-verification', {
      method: 'POST',
      body: JSON.stringify({ email: email.toLowerCase().trim() }),
    });
  } catch (err) {
    const msg = err instanceof Error ? err.message : String(err);
    if (msg === 'Failed to fetch' || msg.includes('fetch') || msg.includes('NetworkError')) {
      throw new Error(
        'Cannot reach the API. Set VITE_API_BASE_URL in Amplify environment variables to your API URL (e.g. https://xxx.execute-api.region.amazonaws.com/prod), then redeploy.'
      );
    }
    throw err;
  }
}

/** Authenticated API (Cognito). Sends Authorization: Bearer <idToken>. Must be exactly this format for API Gateway Cognito authorizer. */
async function authHeaders(): Promise<Record<string, string>> {
  let token = await getAuthToken();
  const h: Record<string, string> = { 'Content-Type': 'application/json' };
  if (token && typeof token === 'string') {
    token = token.trim();
    if (token.toLowerCase().startsWith('bearer ')) token = token.slice(7).trim();
    if (token.length > 0) h['Authorization'] = `Bearer ${token}`;
  }
  return h;
}

/** Options for authFetch. */
type AuthFetchOptions = { noSignOutOn401?: boolean };

/** Run fetch with auth headers; on 401 dispatch AUTH_SESSION_EXPIRED only when we sent a token, unless noSignOutOn401 (so non-critical calls like unread-count don't lock users out). */
async function authFetch(
  input: RequestInfo | URL,
  init?: RequestInit,
  options?: AuthFetchOptions
): Promise<Response> {
  const headers = { ...(init?.headers as Record<string, string> | undefined), ...(await authHeaders()) };
  const r = await fetch(input, { ...init, headers });
  if (r.status === 401 && headers['Authorization'] && !options?.noSignOutOn401) {
    window.dispatchEvent(new Event(AUTH_SESSION_EXPIRED));
  }
  return r;
}

function authUrl(path: string, params?: Record<string, string>): string {
  const base = (config.apiBaseUrl || window.location.origin).replace(/\/$/, '');
  const url = new URL(path.startsWith('/') ? path.slice(1) : path, base + '/');
  if (params) Object.entries(params).forEach(([k, v]) => v != null && v !== '' && url.searchParams.set(k, v));
  return url.toString();
}

/** Parse error message from API response body (e.g. { error: "..." }) so user sees real reason. */
async function errorFromResponse(r: Response): Promise<string> {
  const text = await r.text().catch(() => '');
  try {
    const j = JSON.parse(text) as { error?: string; message?: string };
    if (typeof j?.error === 'string') return j.error;
    if (typeof j?.message === 'string') return j.message;
  } catch {
    /* ignore */
  }
  return text.trim() || r.statusText;
}

/** User profile (role, orgId, name, etc.). */
export interface UserProfile {
  sub: string;
  email: string;
  name: string | null;
  phone: string | null;
  role: 'customer' | 'admin';
  orgId: string;
  createdAt: string;
  updatedAt: string;
  /** Set to false when user is disabled (revoked) in Cognito. */
  enabled?: boolean;
  /** Cognito UserStatus: UNCONFIRMED, CONFIRMED, etc. (from ListUsers merge). */
  cognitoStatus?: string;
  /** True if user exists in Cognito but has never signed in (no DynamoDB profile yet). */
  cognitoOnly?: boolean;
}

export interface AdminCreateUserBody {
  email: string;
  password: string;
  name?: string;
  role?: 'customer' | 'admin';
}

export const userApi = {
  getMe: async (): Promise<UserProfile> => {
    const r = await authFetch(authUrl('user/me'), undefined, { noSignOutOn401: true });
    if (!r.ok) throw new Error(await errorFromResponse(r));
    return r.json() as Promise<UserProfile>;
  },
  updateMe: async (body: { name?: string; phone?: string }): Promise<UserProfile> => {
    const r = await authFetch(authUrl('user/me'), { method: 'PUT', body: JSON.stringify(body) });
    if (!r.ok) throw new Error(await errorFromResponse(r));
    return r.json() as Promise<UserProfile>;
  },
};

/** Inbox entry: one notification per lead per recipient (listing owner or admin). readStatus: new | read. */
export interface InboxEntry {
  notificationId: string;
  userId: string;
  leadId: string;
  listingId: string;
  orgId: string;
  listingOwnerUserId: string | null;
  readStatus: 'new' | 'read';
  createdAt: string;
  updatedAt?: string;
  leadName: string;
  leadEmail: string;
  leadPhone: string | null;
  leadMessage: string;
  /** Source of the lead: listing_detail | card_cta | whatsapp | callback */
  leadSource?: string;
  listingTitleEn: string;
}

export const inboxApi = {
  list: async (params?: { limit?: string }): Promise<{ items: InboxEntry[]; unreadCount: number }> => {
    const r = await authFetch(authUrl('user/inbox', params as Record<string, string>));
    if (!r.ok) throw new Error(await errorFromResponse(r));
    return r.json() as Promise<{ items: InboxEntry[]; unreadCount: number }>;
  },
  getUnreadCount: async (): Promise<{ unreadCount: number }> => {
    const r = await authFetch(authUrl('user/inbox/unread-count'), undefined, { noSignOutOn401: true });
    if (r.status === 401) return { unreadCount: 0 };
    if (!r.ok) throw new Error(await errorFromResponse(r));
    return r.json() as Promise<{ unreadCount: number }>;
  },
  markRead: async (notificationId: string): Promise<InboxEntry> => {
    const r = await authFetch(authUrl(`user/inbox/${notificationId}/read`), { method: 'PUT' });
    if (!r.ok) throw new Error(await errorFromResponse(r));
    return r.json() as Promise<InboxEntry>;
  },
};

export const adminUsers = {
  list: async (): Promise<{ items: UserProfile[] }> => {
    const r = await authFetch(authUrl('admin/users'));
    if (!r.ok) throw new Error(await errorFromResponse(r));
    return r.json() as Promise<{ items: UserProfile[] }>;
  },
  create: async (body: AdminCreateUserBody): Promise<UserProfile> => {
    const r = await authFetch(authUrl('admin/users'), { method: 'POST', body: JSON.stringify(body) });
    if (!r.ok) throw new Error(await errorFromResponse(r));
    return r.json() as Promise<UserProfile>;
  },
  updateRole: async (sub: string, role: 'customer' | 'admin'): Promise<UserProfile> => {
    const r = await authFetch(authUrl(`admin/users/${sub}`), { method: 'PUT', body: JSON.stringify({ role }) });
    if (!r.ok) throw new Error(await errorFromResponse(r));
    return r.json() as Promise<UserProfile>;
  },
  update: async (sub: string, body: { role?: 'customer' | 'admin'; name?: string; phone?: string }): Promise<UserProfile> => {
    const r = await authFetch(authUrl(`admin/users/${sub}`), { method: 'PUT', body: JSON.stringify(body) });
    if (!r.ok) throw new Error(await errorFromResponse(r));
    return r.json() as Promise<UserProfile>;
  },
  delete: async (sub: string, options?: { email?: string }): Promise<void> => {
    const r = await authFetch(authUrl(`admin/users/${sub}`), {
      method: 'DELETE',
      ...(options?.email ? { body: JSON.stringify({ email: options.email }) } : {}),
    });
    if (!r.ok) throw new Error(await errorFromResponse(r));
  },
  disable: async (sub: string): Promise<UserProfile> => {
    const r = await authFetch(authUrl(`admin/users/${sub}/disable`), { method: 'POST' });
    if (!r.ok) throw new Error(await errorFromResponse(r));
    return r.json() as Promise<UserProfile>;
  },
  enable: async (sub: string): Promise<UserProfile> => {
    const r = await authFetch(authUrl(`admin/users/${sub}/enable`), { method: 'POST' });
    if (!r.ok) throw new Error(await errorFromResponse(r));
    return r.json() as Promise<UserProfile>;
  },
  resetPassword: async (sub: string, newPassword: string): Promise<void> => {
    const r = await authFetch(authUrl(`admin/users/${sub}/reset-password`), {
      method: 'POST',
      body: JSON.stringify({ newPassword }),
    });
    if (!r.ok) throw new Error(await errorFromResponse(r));
  },
};

export const adminOrgs = {
  list: async (): Promise<{ items: { orgId: string; name: string }[] }> => {
    const r = await authFetch(authUrl('admin/orgs'));
    if (!r.ok) throw new Error(await errorFromResponse(r));
    return r.json() as Promise<{ items: { orgId: string; name: string }[] }>;
  },
};

export const adminSettings = {
  get: async (): Promise<{ listingLimit: number }> => {
    const r = await authFetch(authUrl('admin/settings'));
    if (!r.ok) throw new Error(await errorFromResponse(r));
    return r.json() as Promise<{ listingLimit: number }>;
  },
  updateListingLimit: async (listingLimit: number): Promise<{ listingLimit: number }> => {
    const r = await authFetch(authUrl('admin/settings'), {
      method: 'PUT',
      body: JSON.stringify({ listingLimit }),
    });
    if (!r.ok) throw new Error(await errorFromResponse(r));
    return r.json() as Promise<{ listingLimit: number }>;
  },
};

export const adminListings = {
  listAll: async (params?: { limit?: string; nextToken?: string }) => {
    const r = await authFetch(authUrl('admin/listings', params as Record<string, string>));
    if (!r.ok) throw new Error(await errorFromResponse(r));
    return r.json() as Promise<{ items: Listing[]; nextToken?: string }>;
  },
  listOrg: async (orgId: string, params?: { limit?: string; nextToken?: string; type?: string; status?: string }) => {
    const r = await authFetch(authUrl(`admin/orgs/${orgId}/listings`, params as Record<string, string>));
    if (!r.ok) throw new Error(await errorFromResponse(r));
    return r.json() as Promise<{ items: Listing[]; nextToken?: string }>;
  },
  get: async (id: string, orgId: string) => {
    const r = await authFetch(authUrl(`admin/listings/${id}`, { orgId }));
    if (!r.ok) throw new Error(await errorFromResponse(r));
    return r.json() as Promise<Listing>;
  },
  create: async (body: Partial<Listing> & { titleEn: string; descriptionEn: string; price: number; type: string }) => {
    const r = await authFetch(authUrl('admin/listings'), { method: 'POST', body: JSON.stringify(body) });
    if (!r.ok) throw new Error(await errorFromResponse(r));
    return r.json() as Promise<{ listingId: string }>;
  },
  update: async (id: string, body: Partial<Listing> & { orgId?: string }) => {
    const r = await authFetch(authUrl(`admin/listings/${id}`), { method: 'PUT', body: JSON.stringify(body) });
    if (!r.ok) throw new Error(await errorFromResponse(r));
    return r.json() as Promise<{ listingId: string }>;
  },
  presign: async (listingId: string, body: { fileName: string; contentType: string; orgId?: string }) => {
    const r = await authFetch(authUrl(`admin/listings/${listingId}/media/presign`), { method: 'POST', body: JSON.stringify(body) });
    if (!r.ok) throw new Error(await errorFromResponse(r));
    return r.json() as Promise<{ uploadUrl: string; mediaKey: string }>;
  },
  approve: async (id: string, orgId: string): Promise<Listing> => {
    const r = await authFetch(authUrl(`admin/listings/${id}/approve`, { orgId }), { method: 'POST' });
    if (!r.ok) throw new Error(await errorFromResponse(r));
    return r.json() as Promise<Listing>;
  },
  unpublish: async (id: string, orgId: string): Promise<Listing> => {
    const r = await authFetch(authUrl(`admin/listings/${id}/unpublish`, { orgId }), { method: 'POST' });
    if (!r.ok) throw new Error(await errorFromResponse(r));
    return r.json() as Promise<Listing>;
  },
  /** Get reporting stats (admin only). Uses noSignOutOn401 so expired token on Reports page doesn't sign user out app-wide. */
  getStats: async (): Promise<AdminStats> => {
    const r = await authFetch(authUrl('admin/stats'), undefined, { noSignOutOn401: true });
    if (!r.ok) throw new Error(await errorFromResponse(r));
    return r.json() as Promise<AdminStats>;
  },
  /** Extend or set expiry date (admin only). */
  extendExpiry: async (
    id: string,
    orgId: string,
    body: { extendByDays?: number; newExpiresAt?: string }
  ): Promise<Listing> => {
    const r = await authFetch(authUrl(`admin/listings/${id}/extend-expiry`, { orgId }), {
      method: 'POST',
      body: JSON.stringify(body),
    });
    if (!r.ok) throw new Error(await errorFromResponse(r));
    return r.json() as Promise<Listing>;
  },
};

export type ListingsResponse = { items: Listing[]; nextToken?: string; hasMore?: boolean };

export interface AdminStats {
  totalListings: number;
  totalLeads: number;
  totalViews: number;
  listingsByStatus: { draft: number; pending_review: number; published: number; unpublished: number };
  listingsByType: { rent: number; sale: number; short_term: number };
}

export interface Listing {
  listingId: string;
  orgId: string;
  type: 'sale' | 'rent' | 'short_term';
  category: 'residential' | 'commercial';
  status: string;
  titleEn: string;
  titleAr?: string;
  descriptionEn: string;
  descriptionAr?: string;
  price: number;
  currency: string;
  location: { city?: string; area?: string; country: string };
  /** Full address for map/search; if set, used for "View on Map". */
  address?: string | null;
  bedrooms?: number;
  bathrooms?: number;
  areaSqm?: number;
  mediaKeys?: string[];
  /** Presigned image URLs (from API when loading listings). */
  mediaUrls?: string[];
  /** First image URL for cards (from API list). */
  thumbnailUrl?: string | null;
  agentId?: string;
  /** Contact email (required for every ad). */
  contactEmail?: string | null;
  /** Contact phone (required for every ad). */
  contactPhone?: string | null;
  /** Unit type: apartment, villa, twin_house, town, studio. */
  unitType?: 'apartment' | 'villa' | 'twin_house' | 'town' | 'studio' | string | null;
  /** Amenity keys (e.g. covered_parking, pets_allowed). */
  amenities?: string[];
  /** View count (incremented on each listing detail view). Only shown to listing owner/admin, not guests. */
  viewCount?: number;
  /** Completeness 0–100 (admin/customer only). */
  completenessScore?: number;
  /** When the listing was published (set on approve). */
  publishedAt?: string | null;
  /** Ad expiry (1 month from publish; admin can extend). */
  expiresAt?: string | null;
  createdAt: string;
  updatedAt: string;
}
